import { useQueryClient } from "@tanstack/react-query";

import { withTracking } from "sandbox/analytics/amplitude";
import {
  useCreateClient,
  useDeleteClient,
  useRotateClientSecret,
} from "sandbox/api/hooks/useSandboxMutations";
import { useClientsList } from "sandbox/api/hooks/useSandboxQueries";

import type {
  CreateCredentialResponse,
  CredentialItem,
  RotateSecretResponse,
} from "../types";

import {
  toCreateCredentialResponse,
  toCredentialListFromDTO,
  toRotateSecretResponse,
} from "./serializers";

const CLIENTS_QUERY_KEY = ["sandbox", "clients"];

export const useCredentials = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useClientsList();

  const credentials: CredentialItem[] = data
    ? toCredentialListFromDTO(data)
    : [];

  const createMutation = useCreateClient();
  const deleteMutation = useDeleteClient();
  const rotateMutation = useRotateClientSecret();

  const create = (name: string): Promise<CreateCredentialResponse> =>
    withTracking("Credential Created", async () => {
      const result = await createMutation.mutateAsync({ name });
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
      return toCreateCredentialResponse(result);
    });

  const remove = (id: string): Promise<void> =>
    withTracking("Credential Deleted", async () => {
      await deleteMutation.mutateAsync({ client_id: id });
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
    });

  const rotate = (id: string): Promise<RotateSecretResponse | null> =>
    withTracking("Credential Secret Rotated", async () => {
      const result = await rotateMutation.mutateAsync({ client_id: id });
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
      return toRotateSecretResponse(result);
    });

  return { credentials, loading: isLoading, create, remove, rotate };
};
